import type { CirclesVariant, Variant } from './gameTypes.js';
import { variantLabel } from './gameTypes.js';
import type { PulseChart } from './pulseChart.js';
import { validatePulseChart } from './pulseChart.js';
import type { CirclesChart } from './circlesChart.js';
import { validateCirclesChart } from './circlesChart.js';
import type { PianoChart } from './pianoChart.js';
import { validatePianoChart } from './pianoChart.js';
import type { GuitarChart } from './guitarChart.js';
import { validateGuitarChart } from './guitarChart.js';
import type { WavesChart } from './wavesChart.js';
import { validateWavesChart } from './wavesChart.js';
import type { JumpChart } from './jumpChart.js';
import { validateJumpChart } from './jumpChart.js';

/**
 * Append the variant suffix to a chart's display title for hard / extreme
 * variants. Normal variants return the bare title unchanged. Used by the
 * builtin chart files so the embedded title stays identifiable in logs,
 * admin tools, and the canvas HUD.
 *
 * Inverse of {@link stripVariantSuffix}.
 */
export function buildVariantTitle(baseTitle: string, variant: CirclesVariant): string {
  if (variant === 'normal') {
    return baseTitle;
  }
  return `${baseTitle} — ${variantLabel(variant)}`;
}

/**
 * Recover a chart's bare title by stripping the trailing ` — Hard` /
 * ` — Extreme` suffix added by {@link buildVariantTitle}. Only strips a
 * known variant suffix — user-built titles that legitimately contain an
 * em-dash (e.g. `"Memory — A Tribute"`) round-trip unchanged.
 *
 * Used by the homepage and play-page header where a coloured badge
 * carries the variant signal so the title text doesn't need to.
 */
export function stripVariantSuffix(chart: { title: string; variant: CirclesVariant }): string {
  if (chart.variant === 'normal') {
    return chart.title;
  }
  const suffix = ` — ${variantLabel(chart.variant)}`;
  if (chart.title.endsWith(suffix)) {
    return chart.title.slice(0, -suffix.length);
  }
  return chart.title;
}

export interface ChartPalette {
  bg: string;
  bgAccent: string;
  player: string;
  pad: string;
  padPerfect: string;
  hazard: string;
  goal: string;
  ui: string;
  [extra: string]: string;
}

/**
 * One audible event in the underlying track. Pre-computed offline by the
 * onset-extractor tool and shipped alongside each audio file. Authors
 * place flip pads only on these beats so every required hit lands on
 * something the player can hear.
 *
 * `kind` tags the type of event so visual cues (downbeat tick vs lead
 * highlight) can differ; `intensity` is roughly relative loudness 0-1.
 */
export interface AudioOnset {
  beat: number;
  kind: 'kick' | 'snare' | 'lead';
  intensity: number;
}

export interface ChartAudio {
  /** Path under public/, e.g. "/audio/tracks/on-and-on.mp3". */
  src: string;
  /**
   * Tempo of the audio file. Must match Chart.bpm; we keep both because
   * Chart.bpm is the canonical clock authority but having the audio's
   * declared tempo here makes the relationship explicit.
   */
  bpm: number;
  /**
   * Milliseconds of silence/intro to skip before beat 0 of the chart.
   * Positive: chart starts later than file start. Negative not allowed
   * since you can't skip backwards into nothing.
   */
  offsetMs: number;
  /** Total duration of the audio file in seconds. Authoring helper. */
  durationSec: number;
  /** Required free-text attribution shown on the play screen + LICENSES.md. */
  attribution: string;
  /**
   * Pre-computed onset list. Builder uses this to snap pads, validator
   * uses it to enforce that every required pad lands on an onset.
   */
  onsets: AudioOnset[];
}

/**
 * Which gameplay system a chart targets. Lives on the chart so the same
 * (slug, mode) can resolve to a Pulse / Circles / Piano / Guitar / Waves
 * map without DB gymnastics — see {@link Chart} for the discriminated
 * union split. Piano and Guitar both use 4-lane mania notes but ship
 * separate slugs so each mode can curate its own song lineup. Waves is
 * a side-scrolling one-button mode (spacewaves.io / Geometry-Dash–wave
 * style) whose obstacles are derived from the same osu-imported JSON
 * Circles uses.
 */
/**
 * Canonical, ordered list of every game mode. The single runtime source
 * of truth — {@link GameMode} is derived from it, and UI that needs to
 * iterate modes (analytics mode tabs, stacked-mode charts) maps over this
 * array so a sixth mode lands everywhere by editing one line. The order
 * is the display order used by mode pickers and chart legends.
 */
export const GAME_MODES = ['pulse', 'circles', 'piano', 'guitar', 'waves', 'jump'] as const;

export type GameMode = (typeof GAME_MODES)[number];

/**
 * Human-readable label for a {@link GameMode}. UI surfaces (top-nav badge,
 * mode picker, results overlay) use this so the title-case strings live in
 * one place — adding a sixth mode only requires updating the union and this
 * switch.
 */
export function modeLabel(mode: GameMode): string {
  switch (mode) {
    case 'pulse': {
      return 'Pulse';
    }
    case 'circles': {
      return 'Circles';
    }
    case 'piano': {
      return 'Piano';
    }
    case 'guitar': {
      return 'Guitar';
    }
    case 'waves': {
      return 'Waves';
    }
    case 'jump': {
      return 'Jump';
    }
  }
}

/**
 * Fields shared by every chart regardless of mode. All mode-specific
 * gameplay data hangs off the discriminated union members
 * ({@link PulseChart}, {@link CirclesChart}).
 */
export interface BaseChart {
  mode: GameMode;
  id: string;
  slug: string;
  title: string;
  description: string;
  bpm: number;
  difficulty: import('./gameTypes.js').Difficulty;
  /**
   * Per-track density variant. Most modes produce the three shared
   * {@link Variant} tiers (normal, hard, extreme); Circles adds a fourth
   * `insane` tier (see {@link CirclesVariant}) so the field is typed to
   * the wider circles set. User maps default to `'normal'` since the
   * builder UI only authors one density. Older serialised charts that
   * pre-date this field decode as `'normal'` via the validator's
   * normalisation pass.
   */
  variant: CirclesVariant;
  totalBeats: number;
  palette: ChartPalette;
  audio: ChartAudio;
  /**
   * Optional credit for the human (or community) who authored the chart's
   * note pattern. Set on osu!-imported Circles maps so the original
   * mapper gets visible attribution in the play UI; left undefined for
   * synthesised charts and user-built maps where the chart author is
   * tracked elsewhere (e.g. via {@link MapDetail.ownerHandle}).
   */
  mapper?: string;
}

/**
 * Discriminated union over the six gameplay modes. Always narrow on
 * `chart.mode` before reaching for mode-specific fields:
 *
 *   if (chart.mode === 'pulse') { ...chart.pads... }
 *   if (chart.mode === 'circles') { ...chart.hitObjects... }
 *   if (chart.mode === 'piano') { ...chart.notes... }
 *   if (chart.mode === 'guitar') { ...chart.notes... }
 *   if (chart.mode === 'waves') { ...chart.obstacles... }
 *   if (chart.mode === 'jump') { ...chart.grid... }
 *
 * Engines, renderers, and game sessions are mode-specific and accept
 * the narrowed type directly (e.g. `PulseChart` not `Chart`). Piano
 * and Guitar share the underlying 4-lane mania engine but route
 * through separate sessions and renderers — the visual identity and
 * chart-source pipelines diverge, the engine doesn't. Waves has its
 * own one-button piecewise-linear engine; Jump has its own one-button
 * piecewise-parabolic (gravity + jump) engine.
 */
export type Chart =
  | PulseChart
  | CirclesChart
  | PianoChart
  | GuitarChart
  | WavesChart
  | JumpChart;

export interface ChartValidationIssue {
  level: 'error' | 'warning';
  message: string;
  beat?: number;
  padId?: string;
}

export interface ChartValidationResult {
  ok: boolean;
  issues: ChartValidationIssue[];
}

export const CHART_LIMITS = {
  maxPads: 800,
  maxHazards: 400,
  maxSections: 200,
  maxBurstChambers: 32,
  maxOnsets: 8000,
  // 1500 beats covers the longest osu!-imported Circles maps in the
  // catalogue. The current ceiling is Nekozilla at chart.bpm=400 (the
  // Extreme diff is mapped on a 1/4-grid relative to the perceived
  // ~128 BPM song) over ~167s of audio = ~1110 beats; the rest sit
  // between 200 and 600 beats. Synthesised Pulse charts stay under
  // 100 beats, so the bump only widens what Circles can ingest.
  maxTotalBeats: 1500,
  maxTitleLength: 64,
  maxDescriptionLength: 500,
  /** Max length of {@link BaseChart.mapper}. Mirrors title length policy. */
  maxMapperLength: 64,
} as const;

export const HEX_COLOUR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const REQUIRED_PALETTE_KEYS = [
  'bg',
  'bgAccent',
  'player',
  'pad',
  'padPerfect',
  'hazard',
  'goal',
  'ui',
] as const;

/**
 * Compute the playable duration of a chart in seconds. Used by the browse
 * page to show "1m 12s" labels so players know how long they're committing
 * to before they start.
 */
export function chartDurationSeconds(bpm: number, totalBeats: number): number {
  if (!Number.isFinite(bpm) || bpm <= 0) {
    return 0;
  }
  if (!Number.isFinite(totalBeats) || totalBeats <= 0) {
    return 0;
  }
  return (totalBeats / bpm) * 60;
}

/**
 * Display-friendly BPM. `chart.bpm` is the engine's beat grid — for
 * osu!-imported Circles charts that's whatever the source mapper
 * declared in the first uninherited timing point, which is sometimes
 * 2× or 3× the perceived song tempo (e.g. 230 BPM Heroes Tonight
 * mapped on a half-time grid, 400 BPM Nekozilla mapped on a 1/3
 * grid). Showing those raw numbers in the UI confuses players who
 * know the real tempo of the song.
 *
 * The fix: halve until the result lands inside [60, 220] — the
 * "perceived" tempo range a human ear groups beats into. Returns
 * `chart.bpm` unchanged when it's already in range. Stable under
 * repeated calls (idempotent), so safe to feed back through if
 * downstream display layers also reach for the perceived value.
 */
export function chartDisplayBpm(bpm: number): number {
  if (!Number.isFinite(bpm) || bpm <= 0) {
    return 0;
  }
  let displayBpm = bpm;
  while (displayBpm > 220) {
    displayBpm /= 2;
  }
  return displayBpm;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '0s';
  }
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) {
    return `${s}s`;
  }
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

/**
 * Find the closest onset (in absolute beat distance) to the given beat.
 * Returns null if the chart has no onsets at all.
 */
export function findNearestOnset(audio: ChartAudio, beat: number): AudioOnset | null {
  if (audio.onsets.length === 0) {
    return null;
  }
  let best = audio.onsets[0];
  let bestDiff = Math.abs(best.beat - beat);
  for (let i = 1; i < audio.onsets.length; i++) {
    const o = audio.onsets[i];
    const diff = Math.abs(o.beat - beat);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = o;
    }
  }
  return best;
}

/**
 * Top-level chart validator. Dispatches by `chart.mode` so each gameplay
 * payload can enforce its own invariants. Returns `{ ok: false, ... }`
 * if the discriminator is missing or unrecognised — that path is only
 * reachable from corrupt DB rows or future modes that this build does
 * not yet understand.
 */
export function validateChart(chart: Chart): ChartValidationResult {
  if (!chart || typeof chart !== 'object') {
    return {
      ok: false,
      issues: [{ level: 'error', message: 'Chart payload must be an object' }],
    };
  }
  const mode = (chart as { mode?: unknown }).mode;
  if (mode === 'pulse') {
    return validatePulseChart(chart as PulseChart);
  }
  if (mode === 'circles') {
    return validateCirclesChart(chart as CirclesChart);
  }
  if (mode === 'piano') {
    return validatePianoChart(chart as PianoChart);
  }
  if (mode === 'guitar') {
    return validateGuitarChart(chart as GuitarChart);
  }
  if (mode === 'waves') {
    return validateWavesChart(chart as WavesChart);
  }
  if (mode === 'jump') {
    return validateJumpChart(chart as JumpChart);
  }
  return {
    ok: false,
    issues: [{ level: 'error', message: `Unknown chart mode: ${String(mode)}` }],
  };
}

// Re-export the per-mode types and helpers so callers can keep importing
// from '@shared/chart' for everything chart-shaped without needing to
// know which sub-module owns each piece. (Per the project rules we avoid
// barrel files; these re-exports exist because the types are split for
// file-size reasons but the concept of "chart" is genuinely one surface.)
export type { PulseChart, FlipPad, Hazard, SectionMarker, BurstChamber } from './pulseChart.js';
export {
  findActiveBurstChamber,
  PAD_ONSET_TOLERANCE_MS,
  MIN_BURST_CHAMBER_BEATS,
  MIN_PAD_TO_BURST_BUFFER_BEATS,
  validatePulseChart,
} from './pulseChart.js';
export type { CirclesChart, CircleHitObject, CircleHitObjectKind } from './circlesChart.js';
export {
  OSU_PLAYFIELD_WIDTH,
  OSU_PLAYFIELD_HEIGHT,
  CIRCLE_ONSET_TOLERANCE_MS,
  MAX_CIRCLE_HIT_OBJECTS,
  validateCirclesChart,
} from './circlesChart.js';
export type { PianoChart } from './pianoChart.js';
export {
  PIANO_ONSET_TOLERANCE_MS,
  MAX_PIANO_NOTES,
  validatePianoChart,
} from './pianoChart.js';
export type { GuitarChart } from './guitarChart.js';
export {
  GUITAR_ONSET_TOLERANCE_MS,
  MAX_GUITAR_NOTES,
  validateGuitarChart,
} from './guitarChart.js';
export type {
  WavesChart,
  WavesObstacle,
  WavesGate,
  WavesSaw,
  WavesGateVisual,
} from './wavesChart.js';
export {
  WAVES_VARIANT_TUNING,
  WAVES_OBSTACLE_PASS_SCORE,
  WAVES_DEFAULT_SPAWN_Y,
  MAX_WAVES_OBSTACLES,
  findGateAtBeat,
  validateWavesChart,
} from './wavesChart.js';
export type { JumpChart } from './jumpChart.js';
export {
  JUMP_VARIANT_TUNING,
  jumpAirtimeBeats,
  jumpApexLeadBeats,
  jumpPeakHeight,
  jumpGroundedCentreY,
  validateJumpChart,
} from './jumpChart.js';
